import { Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { BenchmarkType, IndicatorName, PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import { CarbonStorageBGPotentialChart } from '@/components/Charts/CarbonStorageBGPotential';
import { ChartHeading } from '@/components/Charts/components/ChartHeading';
import { useChartZoom } from '@/components/Charts/components/ChartZoom';
import { GraphFact } from '@/components/Charts/types';
import { getChartDataWithBenchmarks, kgToTonneFormatter } from '@/components/Charts/utils';
import { GraphTileProps } from '@/components/IndicatorCard/GraphTile';
import { useNCCardContext } from '@/components/NCCard/NCCard';
import { useBenchmarksForOrganization } from '@/pages/shared/hooks/useBenchmarkForFact';
import {
  useOrganizationIndicatorGraphFact,
  useOrganizationIndicatorReportingFact,
} from '@/pages/shared/hooks/useOrganizationFact';

import { NoData } from '../../../components/NoData';

export const CarbonGraphTile = (props?: GraphTileProps | null) => {
  const { t } = useTranslation();
  const { analysisType } = useNCCardContext();

  const { zoom, setZoom } = useChartZoom();
  const selectedZoom = props?.zoom ?? zoom; // props.zoom is used when this component is rendered as widget (e.g. measurement pages)
  const setZoomFunc = props?.setZoom ?? setZoom;
  const plotGroupId = props?.plotGroupId ?? null;

  const carbonBgUpliftPotentialFact =
    analysisType === 'total'
      ? IndicatorName.permanent_soil_carbon_uplift_potential_total_graph
      : IndicatorName.permanent_soil_carbon_uplift_potential_per_ha_graph;
  const { [carbonBgUpliftPotentialFact]: carbonBgUpliftPotential } = useOrganizationIndicatorGraphFact<GraphFact[]>(
    carbonBgUpliftPotentialFact,
    null,
    undefined,
    plotGroupId,
  );
  const cbgPotentialData = carbonBgUpliftPotential?.value;

  const carbonBenchmarkFact =
    analysisType === 'total'
      ? IndicatorName.permanent_soil_carbon_total_benchmark
      : IndicatorName.permanent_soil_carbon_per_ha_benchmark;

  const { [carbonBenchmarkFact]: carbonBgLowBenchmark } = useOrganizationIndicatorReportingFact<number>(
    carbonBenchmarkFact,
    null,
    { [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.low },
    plotGroupId,
  );

  const { [carbonBenchmarkFact]: carbonBgModerateBenchmark } = useOrganizationIndicatorReportingFact<number>(
    carbonBenchmarkFact,
    null,
    {
      [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.moderate,
    },
    plotGroupId,
  );

  const { [carbonBenchmarkFact]: carbonHighBenchmark } = useOrganizationIndicatorReportingFact<number>(
    carbonBenchmarkFact,
    null,
    {
      [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.high,
    },
    plotGroupId,
  );

  const cbgBenchmarks = useBenchmarksForOrganization(
    analysisType === 'total'
      ? IndicatorName.permanent_soil_carbon_total_benchmark
      : IndicatorName.permanent_soil_carbon_per_ha_benchmark,
    null,
    kgToTonneFormatter,
    plotGroupId,
  );

  /**
   * We only null check for cbgHistoricData since cbgHistoricData is the primary source data of the graph
   * reference lines are displayed only when cbgHistoricData is rendered
   */
  const noChartData = !cbgPotentialData?.length;
  if (noChartData) {
    return <NoData />;
  }

  const chartData = [
    ...cbgPotentialData,
    ...getChartDataWithBenchmarks({
      chartData: cbgPotentialData,
      benchmarks: { low: carbonBgLowBenchmark, moderate: carbonBgModerateBenchmark, high: carbonHighBenchmark },
    }),
  ];

  return (
    <Stack center data-testid='carbon-graph-tile'>
      <Stack spacing={2.5} className='w-full items-center justify-center px-1 py-6 lg:items-start lg:p-6 lg:pl-1'>
        <ChartHeading
          heading={t('shared.ncaDetail.details.carbon.labels.upliftGraphHeadline')}
          popover={{
            // TODO: MVP-3284 Confirm if the popover copy is correct
            title: t('shared.ncaDetail.details.carbon.explainers.belowTheGroundCarbonStorage.title'),
            body: t('shared.ncaDetail.details.carbon.explainers.belowTheGroundCarbonStorage.body'),
          }}
          className='w-full px-6 sm:px-0'
          zoom={selectedZoom}
          setZoom={setZoomFunc}
        />
        <CarbonStorageBGPotentialChart
          analysisType={analysisType}
          data={chartData}
          benchmarks={cbgBenchmarks}
          showTooltip={true}
          className='relative'
          data-testid='carbon-storage-bg-potential-chart'
          zoom={selectedZoom}
          setZoom={setZoomFunc}
        />
      </Stack>
    </Stack>
  );
};
